import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import NewJob from "@/pages/NewJob";
import NewJobDirect from "@/pages/NewJobDirect";
import FullTimeOrders from "@/pages/FullTimeOrders";
import FullTimeOrderForm from "@/pages/FullTimeOrderForm";
import Presentation from "@/pages/Presentation";
import ADASAnalysis from "@/pages/ADASAnalysis";
import OEMResearch from "@/pages/OEMResearch";
import OEMDetail from "@/pages/OEMDetail";
import Calibration from "@/pages/Calibration";
import Billing from "@/pages/Billing";
import Claims from "@/pages/Claims";
import BodyShopPortal from "@/pages/BodyShopPortal";
import Reports from "@/pages/Reports";
import CRM from "@/pages/CRM";
import AIAssistant from "@/pages/AIAssistant";
import Settings from "@/pages/Settings";
import Scheduling from "@/pages/Scheduling";
import PublicSubmit from "@/pages/PublicSubmit";
import TrackJob from "@/pages/TrackJob";
import VinDecoder from "@/pages/VinDecoder";
import ActivityLogs from "@/pages/ActivityLogs";
import MasterCustomers from "@/pages/master/MasterCustomers";
import MasterInsurance from "@/pages/master/MasterInsurance";
import MasterBodyShops from "@/pages/master/MasterBodyShops";
import MasterTechnicians from "@/pages/master/MasterTechnicians";
import MasterVendors from "@/pages/master/MasterVendors";
import MasterRateTables from "@/pages/master/MasterRateTables";
import MasterCustomServices from "@/pages/master/MasterCustomServices";
import MasterBodyShopDetail from "@/pages/master/MasterBodyShopDetail";
import MasterInsuranceDetail from "@/pages/master/MasterInsuranceDetail";
import MasterCustomerDetail from "@/pages/master/MasterCustomerDetail";
import CashFlow from "@/pages/CashFlow";
import InsuranceNegotiation from "@/pages/InsuranceNegotiation";
import TechApp from "@/pages/TechApp";
import DataReset from "@/pages/DataReset";
import MasterLocations from "@/pages/master/MasterLocations";
import MasterTargets from "@/pages/master/MasterTargets";
import MasterPositionStatements from "@/pages/master/MasterPositionStatements";
import NpsDashboard from "@/pages/NpsDashboard";
import CarrierScorecard from "@/pages/CarrierScorecard";
import Marketplace from "@/pages/Marketplace";
import ApiKeys from "@/pages/ApiKeys";
import ComplianceReports from "@/pages/ComplianceReports";
import PublicBooking from "@/pages/PublicBooking";
import PublicNps from "@/pages/PublicNps";
import EstimateUpload from "@/pages/EstimateUpload";
import CalibrationReport from "@/pages/CalibrationReport";
import CalibrationReportsList from "@/pages/CalibrationReportsList";
import ErrorBoundary from "@/components/ErrorBoundary";
import AdminDuplicates from "@/pages/AdminDuplicates";
import AdminTechGuideFeedback from "@/pages/AdminTechGuideFeedback";
import WorkOrderQueue from "@/pages/WorkOrderQueue";
import WorkOrder from "@/pages/WorkOrder";
import InvoiceNew from "@/pages/InvoiceNew";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Equipment from "@/pages/Equipment";
import EquipmentDetail from "@/pages/EquipmentDetail";
import EquipmentScan from "@/pages/EquipmentScan";
import SalesPipeline from "@/pages/SalesPipeline";
import SalesProspectsList from "@/pages/SalesProspectsList";
import Quotes from "@/pages/Quotes";
import QuoteNew from "@/pages/QuoteNew";
import BibliotecaTrueLine from "@/pages/BibliotecaTrueLine";
import ProspectDetail from "@/pages/ProspectDetail";
import SalesReports from "@/pages/SalesReports";
import SalesCommissions from "@/pages/SalesCommissions";
import Notifications from "@/pages/Notifications";
import AllDataBridgeSetup from "@/pages/AllDataBridgeSetup";
import AllDataBridgePick from "@/pages/AllDataBridgePick";
import PublicPricing from "@/pages/PublicPricing";
import Register from "@/pages/Register";
import AdminPendingUsers from "@/pages/AdminPendingUsers";
import AdminInviteCodes from "@/pages/AdminInviteCodes";
import AdminUsers from "@/pages/AdminUsers";
import WelcomeTour from "@/components/WelcomeTour";
import SalesWorkspace from "@/pages/SalesWorkspace";
import ReferralResolver from "@/pages/ReferralResolver";
import PublicSign from "@/pages/PublicSign";

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    const target = next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login";
    return <Navigate to={target} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" theme="dark" richColors />
            <Routes>
              <Route path="/submit/:token" element={<PublicSubmit />} />
              <Route path="/shop/:token" element={<PublicSubmit />} />
              <Route path="/book/:token" element={<PublicBooking />} />
              <Route path="/rate/:jobToken" element={<PublicNps />} />
              <Route path="/track/:jobToken" element={<TrackJob />} />
              <Route path="/pricing/:token" element={<PublicPricing />} />
              <Route path="/register" element={<Register />} />
              <Route path="/r/:code" element={<ReferralResolver />} />
              <Route path="/sign/:token" element={<PublicSign />} />
              <Route path="/alldata-bridge/pick" element={<RequireAuth><AllDataBridgePick /></RequireAuth>} />
              <Route path="/tech" element={<RequireAuth><TechApp /></RequireAuth>} />
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <Layout />
                  </RequireAuth>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="jobs" element={<Jobs />} />
                <Route path="jobs/new" element={<NewJob />} />
                <Route path="jobs/new-direct" element={<NewJobDirect />} />
                <Route path="fulltime" element={<FullTimeOrders />} />
                <Route path="fulltime/new" element={<FullTimeOrderForm />} />
                <Route path="fulltime/:id" element={<FullTimeOrderForm />} />
                <Route path="apresentacao" element={<Presentation />} />
                <Route path="jobs/:id" element={<JobDetail />} />
                <Route path="adas" element={<ADASAnalysis />} />
                <Route path="oem" element={<OEMResearch />} />
                <Route path="oem/:slug" element={<OEMDetail />} />
                <Route path="calibration" element={<Calibration />} />
                <Route path="billing" element={<Billing />} />
                <Route path="claims" element={<Claims />} />
                <Route path="body-shop" element={<BodyShopPortal />} />
                <Route path="reports" element={<Reports />} />
                <Route path="crm" element={<CRM />} />
                <Route path="ai" element={<AIAssistant />} />
                <Route path="scheduling" element={<Scheduling />} />
                <Route path="settings" element={<Settings />} />
                <Route path="admin/duplicates" element={<AdminDuplicates />} />
                <Route path="admin/tech-guide-feedback" element={<AdminTechGuideFeedback />} />
                <Route path="vin" element={<VinDecoder />} />
                <Route path="admin/activity" element={<ActivityLogs />} />
                <Route path="master/customers" element={<MasterCustomers />} />
                <Route path="master/customers/:id" element={<MasterCustomerDetail />} />
                <Route path="master/insurance" element={<MasterInsurance />} />
                <Route path="master/insurance/:id" element={<MasterInsuranceDetail />} />
                <Route path="master/body-shops" element={<MasterBodyShops />} />
                <Route path="master/body-shops/:id" element={<MasterBodyShopDetail />} />
                <Route path="master/technicians" element={<MasterTechnicians />} />
                <Route path="master/vendors" element={<MasterVendors />} />
                <Route path="master/rate-tables" element={<MasterRateTables />} />
                <Route path="master/custom-services" element={<MasterCustomServices />} />
                <Route path="cash-flow" element={<CashFlow />} />
                <Route path="negotiation" element={<InsuranceNegotiation />} />
                <Route path="admin/data-reset" element={<DataReset />} />
                <Route path="master/locations" element={<MasterLocations />} />
                <Route path="master/targets" element={<MasterTargets />} />
                <Route path="master/positions" element={<MasterPositionStatements />} />
                <Route path="nps" element={<NpsDashboard />} />
                <Route path="scorecard" element={<CarrierScorecard />} />
                <Route path="marketplace" element={<Marketplace />} />
                <Route path="admin/api-keys" element={<ApiKeys />} />
                <Route path="compliance" element={<ComplianceReports />} />
                <Route path="calibration-reports" element={<CalibrationReportsList />} />
                <Route path="calibration-reports/new" element={<EstimateUpload />} />
                <Route path="calibration-reports/:id" element={<ErrorBoundary><CalibrationReport /></ErrorBoundary>} />
                <Route path="work-orders" element={<WorkOrderQueue />} />
                <Route path="work-orders/:id" element={<ErrorBoundary><WorkOrder /></ErrorBoundary>} />
                <Route path="invoices" element={<Billing />} />
                <Route path="invoices/new" element={<InvoiceNew />} />
                <Route path="invoices/:id" element={<ErrorBoundary><InvoiceDetail /></ErrorBoundary>} />
                <Route path="equipment" element={<Equipment />} />
                <Route path="equipment/scan" element={<EquipmentScan />} />
                <Route path="equipment/:id" element={<EquipmentDetail />} />
                <Route path="sales" element={<SalesPipeline />} />
                <Route path="sales/prospects" element={<SalesProspectsList />} />
                <Route path="sales/prospects/:id" element={<ProspectDetail />} />
                <Route path="quotes" element={<Quotes />} />
                <Route path="quotes/new" element={<QuoteNew />} />
                <Route path="quotes/:id" element={<QuoteNew />} />
                <Route path="biblioteca" element={<BibliotecaTrueLine />} />
                <Route path="sales/reports" element={<SalesReports />} />
                <Route path="sales/commissions" element={<SalesCommissions />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="settings/alldata-bridge" element={<AllDataBridgeSetup />} />
                <Route path="admin/pending-users" element={<AdminPendingUsers />} />
                <Route path="admin/invite-codes" element={<AdminInviteCodes />} />
                <Route path="admin/users" element={<AdminUsers />} />
                <Route path="my-workspace" element={<SalesWorkspace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
